<template>
  <b-modal
    ref="userEditModal"
    lazy
    size="lg"
    hide-footer
    class="text-left"
    @hide="onModalHide"
    @hidden="onModalHidden"
  >
    <template slot="modal-header">
      <h5 class="modal-title">
        {{ modalTitle }}
      </h5>
      <div class="pull-right">
        <b-button variant="default" @click="$refs.userEditModal.hide(true)">
          Cancel
        </b-button>
        <b-button @click.prevent="onSaveHandler" variant="primary" class="mr-2">
          <template v-if="!saveLoading">Save</template>
          <template v-else>
            <b-spinner small />
            <strong class="ml-1">Saving...</strong>
          </template>
        </b-button>
      </div>
    </template>
    <v-wait :for="loaderId">
      <HalfCircleSpinner slot="waiting" />
      <template slot="waiting">
        <div class="spinner-message my-3 text-center">
          Loading the user...
        </div>
      </template>

      <template v-if="user">
        <b-form>
          <b-form-group horizontal label="Email">
            <b-form-input readonly plaintext v-model="user.email" />
          </b-form-group>

          <b-form-group horizontal label="Disabled">
            <b-form-checkbox
              switch
              v-model="user.disabled"
              :value="1"
              :unchecked-value="0"
            />
          </b-form-group>
          <b-form-group
            horizontal
            :invalid-feedback="membershipTypeInvalidFeedback"
            label="MembershipType"
          >
            <b-form-select
              v-model="$v.user.society_membership_type_id.$model"
              :options="membershipTypeOptions"
              :state="validateState('society_membership_type_id')"
            >
              <template slot="first">
                <option :value="null" disabled>
                  -- Select --
                </option>
              </template>
            </b-form-select>
          </b-form-group>

          <b-form-group horizontal label="Last name">
            <strong>{{ user.last_name }}</strong>
          </b-form-group>
          <b-form-group horizontal label="First name">
            <strong>{{ user.first_name }}</strong>
          </b-form-group>
          <b-form-group horizontal label="Year of birth">
            <strong>{{ user.year_of_birth }}</strong>
          </b-form-group>
          <b-form-group horizontal label="Ethnicity">
            <strong>{{ ethnicity }}</strong>
          </b-form-group>
          <b-form-group horizontal label="Gender">
            <strong>{{ gender }}</strong>
          </b-form-group>
          <b-form-group
            horizontal
            v-if="isFieldSupportedByMembership('practice_zip')"
            :label="this.isFieldRequiredByMembership('practice_zip') ? 'Practice ZIP Code:' : 'Practice ZIP Code (optional):'"
            class="zip-input"
          >
            <strong>{{ user.practice_zip }}</strong>
          </b-form-group>
          <b-form-group
            horizontal
            v-if="isFieldSupportedByMembership('npi')"
            :label="this.isFieldRequiredByMembership('npi') ? 'NPI:' : 'NPI (optional):'"
          >
            <strong>{{ user.npi }}</strong>
          </b-form-group>
          <b-form-group horizontal label="Society">
            <strong>{{ society }}</strong>
          </b-form-group>
          <b-form-group
            horizontal
            v-if="isFieldSupportedByMembership('specialty')"
            label="Specialty"
          >
            <strong>{{ specialty }}</strong>
          </b-form-group>
        </b-form>
      </template>
    </v-wait>
  </b-modal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';
import Api from '@/providers/Api';
import { handleServerErrorWithValidation } from '@/helpers/ToastedHelper';
import { HalfCircleSpinner } from 'epic-spinners';
import GetMembershipTypeOptionsMixin from '@/mixins/GetMembershipTypeOptionsMixin';

export default {
  name: 'userEditModal',
  mixins: [validationMixin, GetMembershipTypeOptionsMixin],
  components: {
    HalfCircleSpinner,
  },
  props: {
    memberId: {
      type: Number,
      required: true,
    },
  },
  data: function () {
    return {
      user: null,
      saveLoading: false,
    };
  },
  validations: function () {
    return {
      user: {
        society_membership_type_id: {
          required,
        },
      },
    };
  },
  mounted: function () {
    this.showModal();
    this.getUser();
  },
  computed: {
    loaderId: function () {
      return `loading_${this.$options.name}`;
    },
    modalTitle: function () {
      if (this.user) {
        return `Edit User #${this.user.id}: ${this.user.email}`;
      }
      return 'Edit User';
    },
    membershipTypeInvalidFeedback: function () {
      return 'This field if required';
    },
    ethnicity: function () {
      if (!this.user.ethnicity) return '';
      const ethnicity = this.$store.state.ethnicityOptions.find(
        (e) => e.value === this.user.ethnicity
      );
      return ethnicity ? ethnicity.text : '';
    },
    gender: function () {
      if (!this.user.gender_identity) return '';
      const gender = this.$store.state.genderOptions.find(
        (g) => g.value === this.user.gender_identity
      );
      return gender ? gender.text : '';
    },
    society: function () {
      return this.user.society.name;
    },
    specialty: function () {
      const specialty = this.user.society.specialties.find(
        (s) => s.id === this.user.specialty_id
      );
      return specialty ? specialty.title : '';
    },
    membershipTypeOptions: function () {
      return this.$store.state.membershipTypes.items.map((t) => {
        return {
          value: t.id,
          text: t.type_name,
        };
      });
    },
    membershipSupportedFields: function () {
      return this.$store.getters.getMembershipTypesSupportedFields(
        parseInt(this.user.society_membership_type_id)
      );
    },
  },
  methods: {
    showModal: function () {
      this.$refs.userEditModal.show();
    },
    getUser: async function () {
      this.$wait.start(this.loaderId);

      try {
        this.user = await Api.getUserByMemberId(this.memberId);
      } catch (error) {
        handleServerErrorWithValidation(error, this.$toasted);
      } finally {
        this.$wait.end(this.loaderId);
      }

      if (!this.user) {
        this.$refs.userEditModal.hide(true);
      }
    },
    onSaveHandler: function () {
      this.$v.$touch();
      if (this.$v.$anyError) {
        return;
      }
      this.$nextTick(() => {
        this.saveUser();
      });
    },
    saveUser: async function () {
      this.saveLoading = true;
      try {
        await Api.updateUserByMSSAdmin(this.user);
        this.$toasted.success('User saved', { duration: 3000 });
        this.$emit('userSaved', this.user);
        this.$nextTick(() => {
          this.$refs.userEditModal.hide(true);
        });
      } catch (response) {
        handleServerErrorWithValidation(response, this.$toasted);
      } finally {
        this.saveLoading = false;
      }
    },
    onModalHide: function (bvModalEvent) {
      if (this.saveLoading) {
        bvModalEvent.preventDefault();
      }
    },
    onModalHidden: function () {
      this.$emit('modalCancel');
    },
    validateState: function (name) {
      const { $dirty, $error } = this.$v.user[name];
      return $dirty ? !$error : null;
    },
    isFieldSupportedByMembership: function (field) {
      return (
        this.membershipSupportedFields &&
        Array.isArray(this.membershipSupportedFields.supported_fields) &&
        this.membershipSupportedFields.supported_fields.includes(field)
      );
    },
  },
};
</script>

<style scoped></style>
